// src/utils/optimizeAndConvertToWebP.js
import imageCompression from 'browser-image-compression';

/**
 * Optimiza una imagen y la convierte a formato WebP con configuraciones avanzadas
 * @param {File} file - Archivo de imagen original
 * @param {Object} [options] - Opciones personalizadas
 * @param {number} [options.maxSizeMB=1] - Tamaño máximo en MB
 * @param {number} [options.maxWidthOrHeight=1024] - Dimensión máxima (ancho o alto)
 * @param {number} [options.quality=0.75] - Calidad WebP (0-1)
 * @param {boolean} [options.preserveExif=false] - Conservar metadatos EXIF
 * @returns {Promise<{file: File, originalSize: number, optimizedSize: number, reduction: string, suggestedFilename: string}>}
 */
const optimizeAndConvertToWebP = async (file, customOptions = {}) => {
  // Configuración por defecto
  const defaultOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
    fileType: 'image/webp',
    initialQuality: 0.75,
    preserveExif: false,
    ...customOptions
  };

  try {
    if (!file || !file.type.match('image.*')) {
      throw new Error('El archivo no es una imagen válida');
    }

    console.log(`[Optimizer] Procesando: ${file.name} (${Math.round(file.size / 1024)} KB)`);

    // Optimizar y convertir a WebP
    const compressedFile = await imageCompression(file, defaultOptions);
    
    // Generar nombre de archivo sugerido
    const originalName = file.name.substring(0, file.name.lastIndexOf('.'));
    const suggestedFilename = `${originalName || 'image'}_optimized.webp`;
    
    // Calcular reducción
    const originalSize = file.size;
    const optimizedSize = compressedFile.size;
    const reduction = ((originalSize - optimizedSize) / originalSize * 100).toFixed(2);
    
    console.log(`[Optimizer] Resultado: ${suggestedFilename} (${Math.round(optimizedSize / 1024)} KB, reducción: ${reduction}%)`);

    return {
      file: compressedFile,
      originalSize,
      optimizedSize,
      reduction: `${reduction}%`,
      suggestedFilename,
      mimeType: 'image/webp'
    };

  } catch (error) {
    console.error('[Optimizer] Error:', error);
    throw new Error(`Error al optimizar la imagen: ${error.message}`);
  }
};

/**
 * Función para redimensionar una imagen sin comprimir (útil para previsualizaciones)
 */
export const resizeImage = async (file, maxWidth, maxHeight) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          }));
        }, file.type, 0.9);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export default optimizeAndConvertToWebP;